import React, { useEffect, useState } from 'react';
import { toolkitService } from '../services/toolkitService';
import {
  buildQuietOutlookCopy,
  buildSeasonOutlookGroups,
  summarizePlantWeekForecast,
} from './plantingPlantWeekUtils';
import './PlantingPlantWeekOutlook.css';

/**
 * My Plans — one plant-week strip per season with saved plans.
 * Far from window: quiet copy. Near/in: live planting-forecast work days.
 */
export default function PlantingPlantWeekOutlook({ plans = [] }) {
  const groups = buildSeasonOutlookGroups(plans);
  if (!groups.length) return null;

  return (
    <section className="pa-plant-week" aria-label="Plant week by season" data-testid="planting-plant-week">
      <div className="pa-plant-week__header">
        <p className="pa-plant-week__kicker">PLANT WEEK</p>
        <p className="pa-plant-week__lede">
          Near-term work days by season — separate from each plan’s planting window.
        </p>
      </div>
      <div className="pa-plant-week__rows">
        {groups.map((group) => (
          <SeasonOutlookRow key={group.season} group={group} />
        ))}
      </div>
    </section>
  );
}

function SeasonOutlookRow({ group }) {
  const quiet = buildQuietOutlookCopy(group);
  const shouldFetch = Boolean(group.needsForecast && group.point);
  const [status, setStatus] = useState(shouldFetch ? 'loading' : 'idle');
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!shouldFetch) {
      setStatus('idle');
      setSummary(null);
      setError('');
      return undefined;
    }

    let cancelled = false;
    setStatus('loading');
    setError('');

    (async () => {
      try {
        const payload = await toolkitService.getPlantingForecast({
          latitude: group.point.latitude,
          longitude: group.point.longitude,
          season: group.season,
          usdaZoneCode: group.usdaZoneCode || '',
          soilDrainage: group.soilDrainage || '',
          frostSource: group.frostSource || '',
          lastFrostLabel: group.lastFrostLabel || '',
          firstFrostLabel: group.firstFrostLabel || '',
          days: 10,
        });
        if (cancelled) return;
        const next = summarizePlantWeekForecast(payload);
        if (!next.ok) {
          setError(next.error || 'Forecast unavailable');
          setSummary(null);
          setStatus('error');
          return;
        }
        setSummary(next);
        setStatus('ready');
      } catch (err) {
        if (cancelled) return;
        setError(err?.message || 'Could not load plant-week outlook');
        setSummary(null);
        setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    shouldFetch,
    group.season,
    group.point?.latitude,
    group.point?.longitude,
    group.usdaZoneCode,
    group.soilDrainage,
    group.frostSource,
    group.lastFrostLabel,
    group.firstFrostLabel,
  ]);

  return (
    <article className="pa-plant-week__row" data-season={group.season} data-proximity={group.proximity}>
      <div className="pa-plant-week__row-top">
        <span className="pa-plant-week__season">{group.seasonLabel}</span>
        <span className="pa-plant-week__meta">
          {group.planCount} plan{group.planCount === 1 ? '' : 's'}
          {group.windowLabel ? ` · ${group.windowLabel}` : ''}
        </span>
      </div>

      {quiet ? (
        <p className="pa-plant-week__quiet">{quiet}</p>
      ) : null}

      {shouldFetch && status === 'loading' ? (
        <p className="pa-plant-week__status">Checking the next ~10 days…</p>
      ) : null}

      {shouldFetch && status === 'error' ? (
        <p className="pa-plant-week__status pa-plant-week__status--error" role="alert">
          {error}
        </p>
      ) : null}

      {shouldFetch && status === 'ready' && summary ? (
        <div className="pa-plant-week__body">
          {summary.workDays.length ? (
            <ul className="pa-plant-week__days">
              {summary.workDays.map((day) => (
                <li
                  key={day.date}
                  className={`pa-plant-week__day${day.isBest || day.date === summary.bestDay?.date ? ' is-best' : ''}`}
                >
                  <span className="pa-plant-week__day-label">{day.label}</span>
                  <span className="pa-plant-week__day-score">{day.score}</span>
                  {day.rainLabel ? (
                    <span className="pa-plant-week__day-rain">{day.rainLabel}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="pa-plant-week__quiet">
              No strong work days in the outlook yet — keep an eye on rain and drainage.
            </p>
          )}
          {summary.why ? <p className="pa-plant-week__why">{summary.why}</p> : null}
        </div>
      ) : null}
    </article>
  );
}
